import { Box, Paper, Typography, Chip, LinearProgress } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { ClientCardProps, getCardBorder, HEALTH_THRESHOLDS } from '../types';

/**
 * ClientCard - Displays a single client's health summary.
 *
 * Features:
 * - Client name and device count
 * - Alert badge when there are issues
 * - Health bar with color coding (green >= 90%, yellow >= 70%, red < 70%)
 * - Status breakdown dots (up/warning/down counts)
 */
export function ClientCard({ client, isSelected, onClick, sx }: ClientCardProps) {
  const theme = useTheme();

  const healthPercent = client.total > 0
    ? Math.round((client.up / client.total) * 100)
    : 0;

  const hasIssues = client.down > 0 || client.warning > 0;

  const getHealthColor = (percent: number): string => {
    if (percent >= HEALTH_THRESHOLDS.GOOD) return theme.palette.success.main;
    if (percent >= HEALTH_THRESHOLDS.WARNING) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const healthColor = getHealthColor(healthPercent);

  return (
    <Paper
      elevation={isSelected ? 3 : 0}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        p: 2,
        minWidth: 240,
        flex: 1,
        scrollSnapAlign: 'start',
        borderRadius: 2,
        border: isSelected
          ? `2px solid ${theme.palette.primary.main}`
          : getCardBorder(theme),
        cursor: 'pointer',
        transition: 'all 0.2s',
        bgcolor: isSelected
          ? alpha(theme.palette.primary.main, 0.04)
          : 'background.paper',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: 2,
        },
        ...sx,
      }}
    >
      {/* Header: Name and Alert Badge */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 1.5,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" noWrap>
            {client.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {client.total} devices
          </Typography>
        </Box>
        {hasIssues && (
          <Chip
            icon={<WarningIcon sx={{ fontSize: 14 }} />}
            label={client.alerts}
            size="small"
            color="error"
            variant="outlined"
            sx={{ height: 24 }}
          />
        )}
      </Box>

      {/* Health Bar */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Health
          </Typography>
          <Typography variant="caption" fontWeight="bold" color={healthColor}>
            {healthPercent}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={healthPercent}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              bgcolor: healthColor,
            },
          }}
        />
      </Box>

      {/* Status Breakdown Dots */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'success.main',
            }}
          />
          <Typography variant="caption">{client.up}</Typography>
        </Box>
        {client.warning > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'warning.main',
              }}
            />
            <Typography variant="caption">{client.warning}</Typography>
          </Box>
        )}
        {client.down > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'error.main',
              }}
            />
            <Typography variant="caption">{client.down}</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default ClientCard;
